import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { Container, ContainerContent } from '@/components/ui/Container'
import { toast } from '@/hooks/use-toast'
import { SalesOrderSettingsForm } from '@/features/sales-order/settings/SalesOrderSettingsForm'
import {
  hasADateFormat,
  settingsEqual,
  toUpdateRequest,
} from '@/features/sales-order/settings/salesOrderSettingsMappers'
import {
  useWorkflowSalesOrderSettings,
  workflowSalesOrderSettingsQueryKey,
} from '@/features/sales-order/salesOrderSettingsQueries'
import { saveWorkflowSalesOrderSettings } from '@/services/api'
import type { SalesOrderSettings } from '@/types/salesOrderSettings'

/**
 * The workflow's settings: what every customer without settings of their own is validated against.
 */
export function SettingsPage() {
  const { workflowId } = useParams<{ workflowId: string }>()
  const queryClient = useQueryClient()

  const [savedSettings, setSavedSettings] = useState<SalesOrderSettings | null>(null)
  const [draftSettings, setDraftSettings] = useState<SalesOrderSettings | null>(null)

  const { data, isLoading, isError, error } = useWorkflowSalesOrderSettings(workflowId)

  useEffect(() => {
    if (data) {
      setSavedSettings(data)
      setDraftSettings(data)
    }
  }, [data])

  const hasUnsavedChanges =
    savedSettings && draftSettings ? !settingsEqual(savedSettings, draftSettings) : false
  // The backend refuses a save with every date format off, so the button says so before the trip.
  const canSave = !!draftSettings && hasADateFormat(draftSettings)

  const saveMutation = useMutation({
    mutationFn: () => {
      if (!workflowId || !draftSettings) {
        throw new Error('Settings are not loaded')
      }
      return saveWorkflowSalesOrderSettings(workflowId, toUpdateRequest(draftSettings))
    },
    onSuccess: (updated) => {
      setSavedSettings(updated)
      setDraftSettings(updated)
      queryClient.setQueryData(workflowSalesOrderSettingsQueryKey(workflowId!), updated)
      // An open validation view reads its own resolved copy of these settings, so it has to be
      // asked again rather than left showing what was true before the save.
      void queryClient.invalidateQueries({ queryKey: ['customer-order-detail'] })
      toast({ title: 'Settings saved' })
    },
    onError: (err: Error) => {
      toast({ title: 'Could not save settings', description: err.message, variant: 'destructive' })
    },
  })

  if (!workflowId) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-gray-500">
        No workflow selected.
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-red-600">
        {error instanceof Error ? error.message : 'Could not load settings'}
      </div>
    )
  }

  if (isLoading || !draftSettings) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-gray-400">Loading…</div>
    )
  }

  return (
    <div className="container mx-auto">
      <Container>
        <ContainerContent className="space-y-8 py-6">
          <header className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Settings</h1>
              <p className="mt-2 text-sm text-gray-600">
                Changes to these settings will affect all users of the organization
              </p>
            </div>
            <Button variant="cta"
              type="button"
              disabled={!hasUnsavedChanges || !canSave || saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
            >
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </header>

          <SalesOrderSettingsForm settings={draftSettings} onChange={setDraftSettings} />
        </ContainerContent>
      </Container>
    </div>
  )
}
